import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Typography, useTheme, Dialog, DialogContent, Paper } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { PCBQuestions } from "./questions";
import { PCBPlayBook } from "./playbook";
import ProgressiveAnalysisTracker from "./ProgressiveAnalysisTracker";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";
import { buildPcbRunPlayBook, isPcbProductValid, pcbCostErrorMessage, validatedPcbCostResponse, validatedPcbStreamCost, validatedPcbEffectiveLayout } from "./pcbCosting";

const DEFAULT_BREAKDOWN_ORDER = ["material", "packaging", "consumables", "process", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  packaging: "Consumables",
  consumables: "Consumables",
  process: "Process",
  ohp: "Overhead & Profits",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions = [],
  productData,
  playBookData,
  perlColors,
  perlTypography,
}) {
  if (!assumptions || assumptions.length === 0) return null;

  const productInfo = [];

  if (productData?.productName) {
    productInfo.push(`Product Name: ${productData.productName}`);
  }

  if (productData?.boardType) {
    productInfo.push(`Board Type: ${productData.boardType}`);
  }

  const boardLength = productData?.boardLengthMm;
  const boardWidth = productData?.boardWidthMm;
  if (boardLength || boardWidth) {
    const lengthText = boardLength ? `L: ${boardLength}mm` : null;
    const widthText = boardWidth ? `W: ${boardWidth}mm` : null;
    const sizeText = [lengthText, widthText].filter(Boolean).join(", ");
    if (sizeText) {
      productInfo.push(`Board Size: ${sizeText}`);
    }
  }

  const boardThickness = playBookData?.boardThicknessMm ?? productData?.boardThicknessMm;
  if (boardThickness) {
    productInfo.push(`Board Thickness: ${boardThickness}mm`);
  }

  const panelSize = playBookData?.panelSizeIn ?? productData?.panelSizeIn;
  if (panelSize) {
    productInfo.push(`Panel Size: ${panelSize}`);
  }

  const batchSize = productData?.estimatedBatchSize;
  const batchNumber = Number(batchSize);
  if (Number.isFinite(batchNumber) && batchNumber > 0) {
    productInfo.push(`Estimated Batch Size: ${batchNumber.toLocaleString()} units`);
  }

  if (productData?.manufacturingCountry) {
    productInfo.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
  }

  if (playBookData?.materialType) {
    productInfo.push(`Material Type: ${playBookData.materialType}`);
  }

  if (playBookData?.materialTg) {
    productInfo.push(`Material Tg: ${playBookData.materialTg}`);
  }

  if (playBookData?.boardFinish) {
    productInfo.push(`Board Finish: ${playBookData.boardFinish}`);
  }

  const layerCount = Number(playBookData?.layerCount);
  if (Number.isFinite(layerCount) && layerCount > 0) {
    productInfo.push(`Layer Count: ${layerCount}`);
  }

  const copperOz = Number(playBookData?.cuAlThicknessOz);
  if (Number.isFinite(copperOz) && copperOz > 0) {
    productInfo.push(`Copper Thickness: ${copperOz} oz`);
  }

  const drilledHoles = Number(playBookData?.drilledHoleCount);
  if (Number.isFinite(drilledHoles) && drilledHoles > 0) {
    productInfo.push(`Drilled Holes: ${drilledHoles.toLocaleString()}`);
  }

  const blindHoles = Number(playBookData?.blindHoleCount);
  if (Number.isFinite(blindHoles) && blindHoles > 0) {
    productInfo.push(`Blind Holes: ${blindHoles.toLocaleString()}`);
  }

  const buriedHoles = Number(playBookData?.buriedHoleCount);
  if (Number.isFinite(buriedHoles) && buriedHoles > 0) {
    productInfo.push(`Buried Holes: ${buriedHoles.toLocaleString()}`);
  }

  const goldFingers = Number(playBookData?.goldFingerCount);
  if (Number.isFinite(goldFingers) && goldFingers > 0) {
    productInfo.push(`Gold Fingers: ${goldFingers.toLocaleString()}`);
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          bgcolor: perlColors.surface,
          border: `1px solid ${perlColors.border}`,
          borderRadius: 2,
          mt: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "18px",
                fontFamily: perlTypography?.family,
                letterSpacing: "0.3px",
                color: perlColors.textPrimary,
              }}
            >
              Product Information
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {productInfo.map((item, idx) => {
                const itemStr =
                  typeof item === "string"
                    ? item
                    : typeof item === "object" && item !== null
                      ? item.value || item.name || item.reason || JSON.stringify(item)
                      : String(item || "");
                return (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography?.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {itemStr}
                  </Typography>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              width: "1px",
              bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
              alignSelf: "stretch",
            }}
          />

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "18px",
                fontFamily: perlTypography?.family,
                letterSpacing: "0.3px",
                color: perlColors.textPrimary,
              }}
            >
              Assumptions
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {assumptions.map((assumption, idx) => {
                const assumptionStr =
                  typeof assumption === "string"
                    ? assumption
                    : typeof assumption === "object" && assumption !== null
                      ? assumption.value ||
                        assumption.name ||
                        assumption.reason ||
                        JSON.stringify(assumption)
                      : String(assumption || "");
                return (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography?.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumptionStr}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default function PCBPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlTypography = theme.custom?.perlTypography || {};
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [productImageError, setProductImageError] = useState("");
  const imageQuoteSnapshotRef = useRef(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const imageRequestGenerationRef = useRef(0);
  useEffect(() => () => { imageRequestGenerationRef.current += 1; }, []);
  const quoteVersion = useRef(0);
  const productSnapshot = useRef("null");
  const clearQuote = useCallback(() => {
    quoteVersion.current += 1;
    imageRequestGenerationRef.current += 1;
    imageQuoteSnapshotRef.current = null;
    setProductImageError("");
    setGeneratingImage(false);
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    setProductImageUrl(null);
  }, []);

  const normalizeAssumptions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      return raw
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (typeof raw === "object") {
      return Object.values(raw)
        .flat()
        .map((v) => (typeof v === "string" ? v : JSON.stringify(v)))
        .filter(Boolean);
    }
    return [];
  };

  const normalizeKeyInsights = (raw) => {
    if (!raw) return null;
    if (Array.isArray(raw)) {
      return { cost_savings: raw, strategic: [] };
    }
    const source =
      raw.key_insights || raw.KeyInsights || raw.KeyInsightsJson || raw.keyInsights || raw;
    if (!source) return null;
    if (Array.isArray(source)) {
      return { cost_savings: source, strategic: [] };
    }
    return {
      cost_savings: source.cost_savings || source.costSavings || [],
      strategic: source.strategic || source.strategic_recommendations || [],
    };
  };

  // PCB best-in-class defaults (aligned to PlayBook component)
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = useCallback((data, options = {}) => {
    const next = isPcbProductValid(data) ? data : null;
    const snapshot = JSON.stringify(next);
    if (snapshot === productSnapshot.current && !options.reset) return;
    productSnapshot.current = snapshot;
    clearQuote();
    setProductData(next);
    setPlayBookData((prev) => {
      if (options.reset) return null;
      if (!prev) return null;
      if (!next) return { ...prev, overhead: null };
      try { return buildPcbRunPlayBook(next, prev); }
      catch { return { ...prev, boardType: next.boardType, boardLengthMm: next.boardLengthMm, boardWidthMm: next.boardWidthMm, overhead: null }; }
    });
  }, [clearQuote]);

  const handlePlayBookChange = useCallback((field, value, options = {}) => {
    if (!options.derived) clearQuote();
    setPlayBookData((prev) => {
      if (!prev || (prev[field] === value && (options.derived || prev.overrideFields?.[field] === (options.override !== false)))) return prev;
      return { ...prev, [field]: value, overhead: options.derived ? prev.overhead : null,
        overrideFields: options.derived ? prev.overrideFields : { ...(prev.overrideFields || {}), [field]: options.override !== false } };
    });
  }, [clearQuote]);

  // Helper function to analyze cost, risk, and substitutes with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      const costResponse = await apiFetch("/api/pcb/analyze-cost", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      const costResult = validatedPcbCostResponse(await costResponse.json());
      console.log("✅ Cost analysis complete:", costResult);
      // enrich with assumptions/key insights if present
      setCostData({
        ...costResult,
        assumptions:
          costResult.assumptions ||
          costResult.Assumptions ||
          normalizeAssumptions(costResult.assumptions || costResult.Assumptions),
        key_insights: normalizeKeyInsights(costResult.key_insights || costResult.KeyInsights),
      });
      setRiskData(null);
      setSubstituteData(null);

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${pcbCostErrorMessage(error)}`);
      throw error;
    }
  };

  const analyzeCostWithDataV2 = async (recipe, playbook, product) => {
    try {
      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/pcb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/pcb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/pcb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      const costResult = validatedPcbCostResponse(await costResponse.json());
      setCostData(costResult);

      if (riskResponse.ok) {
        setRiskData(await riskResponse.json());
      } else {
        setRiskData(null);
      }

      if (substituteResponse.ok) {
        setSubstituteData(await substituteResponse.json());
      } else {
        setSubstituteData(null);
      }

      await generateProductImageWithData(product, playbook);
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${pcbCostErrorMessage(error)}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithDataV2(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const generateProductImageWithData = async (product, playbook, expectedVersion) => {
    if (!product) return;

    if (expectedVersion !== undefined && expectedVersion !== quoteVersion.current) return;
    const requestGeneration = ++imageRequestGenerationRef.current;
    const imageQuoteVersion = quoteVersion.current;
    imageQuoteSnapshotRef.current = JSON.parse(JSON.stringify({ product, playbook }));
    setProductImageError("");
    setProductImageUrl(null);
    setGeneratingImage(true);

    try {
      /*
      const imagePayload = {
        productName: product.productName,
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: product.boardType || "Multilayer PCB",
        // Force bare PCB visualization (no retail pack)
        primaryPackaging: "Bare PCB",
        forceBarePcb: true,
        skuWt: product.skuWt || "",
        skuWtUnit: product.skuWtUnit || "g",
        marketPositioning: product.boardType || "PCB",
        style: "professional",
        imageContext:
          product.boardLengthMm && product.boardWidthMm
            ? `Bare printed circuit board on a neutral inspection backdrop. Board size ${product.boardLengthMm}mm x ${product.boardWidthMm}mm, thickness ${product.boardThicknessMm || "1.6"}mm. No packaging, no bag, no label. Show the PCB itself with ENIG pads and vias.`
            : "Bare printed circuit board on a neutral inspection backdrop. No packaging, no bag, no label. Show the PCB itself with ENIG pads and vias.",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      const response = await apiFetch("/api/ai/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });
      */

      const imagePayload = {
        Tower: "EE",
        Commodity: "PCB",
        BoardType: product.boardType || playbook?.boardType || "PCB",
      };

      console.log("Generating PCB product image:", imagePayload);

      const response = await apiFetch("/api/pcb/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(typeof errorData.error === "string" ? errorData.error : "Failed to generate image");
      }

      const data = await response.json();
      if (requestGeneration !== imageRequestGenerationRef.current || imageQuoteVersion !== quoteVersion.current) return;
      const imageUrl = data.image_url || data.imageUrl;
      if (typeof imageUrl !== "string" ||
          !(/^(?:data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$|https:\/\/)/.test(imageUrl))) {
        throw new Error("Image service returned no usable product image.");
      }
      setProductImageUrl(imageUrl);
    } catch (error) {
      if (requestGeneration !== imageRequestGenerationRef.current || imageQuoteVersion !== quoteVersion.current) return;
      console.error("Product image generation failed:", error);
      setProductImageError(error?.status === 401 ? error.message
        : "The product image could not be generated. The calculated cost is unchanged.");
    } finally {
      if (requestGeneration === imageRequestGenerationRef.current) setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    const snapshot = imageQuoteSnapshotRef.current;
    if (!snapshot || !costData) return;
    return generateProductImageWithData(snapshot.product, snapshot.playbook);
  };

  // Merge best-in-class defaults with any user overrides so reruns keep user input
  const buildRunPlayBook = (product, existingPlayBook) => buildPcbRunPlayBook(product, existingPlayBook);

  const handleQuickCost = async () => {
    if (!isPcbProductValid(productData)) return false;
    clearQuote();
    const runVersion = quoteVersion.current;

    setLoadingMessage("Running quick cost...");
    setIsProcessing(true);

    try {
      const playbook = buildRunPlayBook(productData, playBookData);
      setPlayBookData(playbook);
      setRecipeData([]); // PCB flow does not use food recipes

      const response = await apiFetch("/api/pcb/quick-cost", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData,
          playBookData: {
            ...playbook,
            // propagate optimizer metrics to backend for validation context
            panelBoardsPerPanel: playbook?.boardsPerPanel,
            panelUtilizationPct: playbook?.utilization,
          },
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to generate quick cost";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const rawCostResult = await response.json();
      if (quoteVersion.current !== runVersion) return false;
      const validatedCost = validatedPcbCostResponse(rawCostResult);
      const costResult = { ...validatedCost,
        effective_layout: validatedPcbEffectiveLayout(validatedCost.effective_layout, productData, playbook) };
      const nextPlayBook = { ...playbook, overhead: costResult.overhead,
        boardsPerPanel: costResult.effective_layout.boardsPerPanel,
        utilization: costResult.effective_layout.utilizationPct,
        orientationDegrees: costResult.effective_layout.orientationDegrees };
      setCostData(costResult);
      setPlayBookData(nextPlayBook);
      // Fetch heuristic risk/substitute insights
      try {
        const payload = { recipe: [], playBookData: playbook, productData };
        const [riskRes, subsRes] = await Promise.all([
          apiFetch("/api/pcb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }),
          apiFetch("/api/pcb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }),
        ]);
        const risk = riskRes.ok ? await riskRes.json() : null;
        const substitutes = subsRes.ok ? await subsRes.json() : null;
        if (quoteVersion.current !== runVersion) return false;
        setRiskData(risk);
        setSubstituteData(substitutes);
      } catch (inner) {
        console.warn("Risk/Substitute fetch failed:", inner);
        if (quoteVersion.current === runVersion) {
          setRiskData(null);
          setSubstituteData(null);
        }
      }
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

      if (quoteVersion.current !== runVersion) return false;
      await generateProductImageWithData(productData, playbook, runVersion);
      return true;
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to generate quick cost: ${pcbCostErrorMessage(error)}`);
      return false;
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const runFallbackQuickCostWithTracker = async () => {
    const fallbackSteps = [
      { id: "step-1", label: "Preparing PlayBook...", status: "in-progress", message: "" },
      { id: "step-2", label: "Running Cost Analysis...", status: "pending", message: "" },
      { id: "step-3", label: "Fetching Insights...", status: "pending", message: "" },
    ];

    setProgressSteps(fallbackSteps);
    setCurrentProgressStep(1);
    setOverallProgress(5);
    setShowProgressTracker(true);
    setIsProcessing(true);
    setLoadingMessage("Running quick cost...");

    let localProgress = 5;
    const timer = setInterval(() => {
      localProgress = Math.min(localProgress + 7, 92);
      setOverallProgress(localProgress);
    }, 600);

    try {
      // Step 1 -> Step 2
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 0
            ? { ...step, status: "completed" }
            : idx === 1
              ? { ...step, status: "in-progress" }
              : step,
        ),
      );

      if (!(await handleQuickCost())) {
        setProgressSteps((prev) => prev.map((step) => step.status === "in-progress" ? { ...step, status: "error", message: "Pricing did not complete" } : step));
        return;
      }

      // Step 2 -> Step 3
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 1
            ? { ...step, status: "completed" }
            : idx === 2
              ? { ...step, status: "in-progress" }
              : step,
        ),
      );

      setOverallProgress(100);
      setProgressSteps((prev) => prev.map((step) => ({ ...step, status: "completed" })));
      setCurrentProgressStep(3);
    } finally {
      clearInterval(timer);
      setTimeout(() => {
        setShowProgressTracker(false);
        setIsProcessing(false);
      }, 600);
    }
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    const totalSteps = 8;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Initializing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));

    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setLoadingMessage("Running quick cost...");

    let maxProgress = 0;
    let completed = false;

    try {
      const requestData = {
        productName: productData.productName || "Printed Circuit Board",
        country: productData.manufacturingCountry || "USA",
        boardType: productData.boardType || "PCB",
        boardLengthMm: productData.boardLengthMm,
        boardWidthMm: productData.boardWidthMm,
        boardThicknessMm: productData.boardThicknessMm,
        panelSizeIn: productData.panelSizeIn,
        annualVolume: productData.annualVolume,
        skuWeight: productData.skuWt || "",
        skuWeightUnit: productData.skuWtUnit || "g",
      };

      const response = await apiFetch("/api/pcb/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Streaming not available (status ${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;
          const dataMatch = event.match(/^data:\s*(.+)$/m);
          if (!dataMatch) continue;

          let eventData;
          try {
            eventData = JSON.parse(dataMatch[1]);
          } catch (err) {
            console.warn("Could not parse SSE event", err);
            continue;
          }

          if (eventData.type === "start") {
            const newTotal = eventData.total_steps || totalSteps;
            const steps = Array.from({ length: newTotal }, (_, i) => ({
              id: `step-${i + 1}`,
              label: `Step ${i + 1}`,
              status: "pending",
              message: "",
            }));
            setProgressSteps(steps);
          } else if (eventData.type === "progress") {
            const stepIndex = (eventData.step || 1) - 1;
            setProgressSteps((prev) => {
              const updated = [...prev];
              if (updated[stepIndex]) {
                let mappedStatus = eventData.status;
                if (eventData.status === "running") mappedStatus = "in-progress";
                if (eventData.status === "success") mappedStatus = "completed";
                updated[stepIndex] = {
                  ...updated[stepIndex],
                  label: eventData.label || updated[stepIndex].label,
                  status: mappedStatus || updated[stepIndex].status,
                  message: eventData.message || updated[stepIndex].message,
                  error: eventData.error,
                };
              }
              return updated;
            });

            setCurrentProgressStep(eventData.step || stepIndex + 1);
            const computedProgress =
              eventData.progress ?? ((eventData.step || 1) / (eventData.total || totalSteps)) * 100;
            if (computedProgress > maxProgress) {
              maxProgress = computedProgress;
              setOverallProgress(computedProgress);
            }
          } else if (eventData.type === "complete") {
            const validatedCost = validatedPcbStreamCost(eventData);
            completed = true;
            maxProgress = 100;
            setOverallProgress(100);
            setProgressSteps((prev) =>
              prev.map((step) => ({
                ...step,
                status: "completed",
                message: "Complete",
              })),
            );

            if (eventData.playBookData) {
              const unwrappedPlaybook =
                eventData.playBookData.playBookData || eventData.playBookData;
              setPlayBookData(unwrappedPlaybook);
            }

            if (eventData.riskData) {
              const unwrappedRisk = eventData.riskData.riskData || eventData.riskData;
              setRiskData(unwrappedRisk);
            }

            if (eventData.substituteData) {
              const unwrappedSubs =
                eventData.substituteData.substituteData || eventData.substituteData;
              setSubstituteData(unwrappedSubs);
            }

            if (eventData.productImage?.imageUrl) {
              setProductImageUrl(eventData.productImage.imageUrl);
            }

            if (eventData.ShouldCostResult) {
              setCostData((prev) => ({
                ...(prev || {}),
                breakdown: validatedCost.breakdown,
                total: validatedCost.breakdown.total,
                assumptions:
                  eventData.assumptions ||
                  eventData.Assumptions ||
                  normalizeAssumptions(
                    eventData.assumptions || eventData.Assumptions || prev?.assumptions,
                  ),
                key_insights:
                  normalizeKeyInsights(
                    eventData.KeyInsightsJson ||
                      eventData.key_insights ||
                      eventData.KeyInsights ||
                      prev?.key_insights,
                  ) || null,
              }));
            }

            if (eventData.RecipeData) {
              setRecipeData(eventData.RecipeData);
            }

            // Fallback image generation if SSE did not return an image
            if (!eventData.productImage?.imageUrl) {
              try {
                await generateProductImageWithData(productData, playBookData);
              } catch (imgErr) {
                console.warn("PCB image generation fallback failed:", imgErr);
              }
            }

            setActiveSection("results");
            setSectionStarted((prev) => ({
              ...prev,
              playbook: true,
              results: true,
            }));

            setTimeout(() => {
              setShowProgressTracker(false);
              setIsProcessing(false);
            }, 500);
            break;
          } else if (eventData.type === "error") {
            throw new Error(eventData.error || eventData.message || "Analysis failed");
          }
        }
      }

      if (!completed) {
        throw new Error("Progress stream ended without completion");
      }
    } catch (error) {
      if (error?.code === "PCB_COST_RESPONSE_INVALID") {
        clearQuote();
        setProgressSteps(prev => prev.map(step => ({ ...step, status: "error", message: pcbCostErrorMessage(error) })));
        setShowProgressTracker(false);
        setIsProcessing(false);
        setLoadingMessage("");
        alert(pcbCostErrorMessage(error));
        return;
      }
      console.error("Progressive quick cost failed, falling back:", error);
      await runFallbackQuickCostWithTracker();
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with formulation, manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);

  const normalizedAssumptions = useMemo(
    () => normalizeAssumptions(costData?.assumptions ?? costData?.Assumptions),
    [costData],
  );

  const normalizedKeyInsights = useMemo(
    () =>
      normalizeKeyInsights(
        costData?.key_insights ??
          costData?.KeyInsights ??
          costData?.KeyInsightsJson ??
          costData?.keyInsights,
      ),
    [costData],
  );

  const costTotal = useMemo(() => {
    const declaredTotal = costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows);
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Electronics"
          industry="Electronics"
          commodity="PCB"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height: "92%" }}>
                <PCBQuestions
                  onComplete={handleProductComplete}
                  onDataChange={handleProductComplete}
                  initialData={productData}
                />
              </Box>
            )}

            {/* {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )} */}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <PCBPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 700, color: perlColors.textPrimary }}
                >
                  Analysis Report
                </Typography>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    imageError={productImageError}
                    onRetryImage={generateProductImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={normalizedAssumptions}
                    keyInsights={normalizedKeyInsights}
                    showFormulationHint={false}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: normalizedAssumptions,
                      productData,
                      playBookData,
                      perlColors,
                      perlTypography,
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button - Show only on product section when data is available */}
        {sectionStarted.product && (
          <QuickCostButton
            onClick={runFallbackQuickCostWithTracker}
            disabled={!isPcbProductValid(productData)}
            loading={isProcessing && !showProgressTracker}
            brandColor="#ff6034"
          />
        )}

        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
